<template>
  <div class="popup_wrap lithium_battery_pop" style="width:520px;">
    <button type="button" class="layer_close" @click="$emit('close')">close</button>
    <div class="popup_cont">
      <h1 class="page_title">Lithium Battery</h1>
      <div class="content_box lithium_battery_box">
        <p class="lithium_battery_desc">
          Please select whether the cargo contains lithium batteries.<br>
          <strong>(Multiple selections are allowed.)</strong>
        </p>
        <ul class="lithium_battery_options">
          <li v-for="option in batteryTypeOptions" :key="option.typeCd">
            <input
              :id="'lithiumBattery_' + option.typeCd"
              v-model="selectedTypeCodes"
              type="checkbox"
              :value="option.typeCd"
              @change="clearNoLithium"
            >
            <label :for="'lithiumBattery_' + option.typeCd">{{ option.label }}</label>
          </li>
          <li>
            <input
              id="lithiumBattery_05"
              v-model="noLithiumYn"
              type="checkbox"
              true-value="Y"
              false-value="N"
              @change="clearBatteryTypes"
            >
            <label for="lithiumBattery_05">{{ noLithiumLabel }}</label>
          </li>
        </ul>
      </div>
      <div class="mt10 text_center">
        <a class="button blue lg" href="#" @click.prevent="confirmSelection">Confirm</a>
        <a class="button gray lg" href="#" @click.prevent="$emit('close')">Close</a>
      </div>
    </div>
  </div>
</template>

<script>
import commons from '@/api/services/commons'

const FALLBACK_CODE_LIST = [
  { cd: '00', cdNm: '5', dsc: '05', rmk: 'No lithium batteries included' },
  { cd: '01', cdNm: '1', dsc: '01', rmk: '<DG> Lithium Ion Battery' },
  { cd: '02', cdNm: '2', dsc: '02', rmk: '<DG> Lithium metal Battery' },
  { cd: '03', cdNm: '3', dsc: '03', rmk: '<Non-DG> Lithium Ion Battery' },
  { cd: '04', cdNm: '4', dsc: '04', rmk: '<Non-DG> Lithium metal Battery' },
  { cd: '05', cdNm: '1,2', dsc: '01;02' },
  { cd: '06', cdNm: '1,3', dsc: '01;03' },
  { cd: '07', cdNm: '1,4', dsc: '01;04' },
  { cd: '08', cdNm: '2,3', dsc: '02;03' },
  { cd: '09', cdNm: '2,4', dsc: '02;04' },
  { cd: '10', cdNm: '3,4', dsc: '03;04' },
  { cd: '11', cdNm: '1,2,3', dsc: '01;02;03' },
  { cd: '12', cdNm: '1,2,4', dsc: '01;02;04' },
  { cd: '13', cdNm: '1,3,4', dsc: '01;03;04' },
  { cd: '14', cdNm: '2,3,4', dsc: '02;03;04' },
  { cd: '15', cdNm: '1,2,3,4', dsc: '01;02;03;04' }
]

const TYPE_LABELS = {
  '01': '<DG> Lithium Ion Battery',
  '02': '<DG> Lithium metal Battery',
  '03': '<Non-DG> Lithium Ion Battery',
  '04': '<Non-DG> Lithium metal Battery'
}

export default {
  name: 'LithiumBatteryPop',
  props: {
    parentInfo: {
      type: Object,
      default: () => ({ btrCd: '' })
    }
  },
  data () {
    return {
      batteryCodeList: FALLBACK_CODE_LIST,
      selectedTypeCodes: [],
      noLithiumYn: 'N'
    }
  },
  computed: {
    batteryTypeOptions () {
      return ['01', '02', '03', '04'].map(typeCd => {
        const item = this.batteryCodeList.find(code => code.cd === typeCd)
        return { typeCd, label: item && item.rmk ? item.rmk : TYPE_LABELS[typeCd] }
      })
    },
    noLithiumLabel () {
      const item = this.batteryCodeList.find(code => code.cd === '00')
      return item && item.rmk ? item.rmk : 'No lithium batteries included'
    }
  },
  created () {
    this.init()
  },
  methods: {
    async init () {
      try {
        const commonCodes = await commons.getCommonsCodesCommon(['CS139'])
        if (commonCodes.CS139 && commonCodes.CS139.length > 0) {
          this.batteryCodeList = commonCodes.CS139
        }
      } catch (e) {
        this.batteryCodeList = FALLBACK_CODE_LIST
      }
      this.restoreSelection(this.parentInfo.btrCd)
    },
    restoreSelection (btrCd) {
      if (!btrCd) return
      if (btrCd === '00') {
        this.noLithiumYn = 'Y'
        this.selectedTypeCodes = []
        return
      }
      const item = this.batteryCodeList.find(code => code.cd === btrCd)
      if (item && item.dsc) {
        this.selectedTypeCodes = this.normalizeTypeCodes(item.dsc.split(';'))
        this.noLithiumYn = 'N'
      }
    },
    clearNoLithium () {
      if (this.selectedTypeCodes.length > 0) this.noLithiumYn = 'N'
    },
    clearBatteryTypes () {
      if (this.noLithiumYn === 'Y') this.selectedTypeCodes = []
    },
    normalizeTypeCodes (typeCodes) {
      return typeCodes
        .map(code => code.trim())
        .filter(code => ['01', '02', '03', '04'].includes(code))
        .sort()
    },
    findCombinationCode () {
      const selectedKey = this.normalizeTypeCodes(this.selectedTypeCodes).join(';')
      const item = this.batteryCodeList.find(code => {
        if (code.cd === '00' || !code.dsc) return false
        return this.normalizeTypeCodes(code.dsc.split(';')).join(';') === selectedKey
      })
      return item ? item.cd : ''
    },
    confirmSelection () {
      if (this.noLithiumYn !== 'Y' && this.selectedTypeCodes.length === 0) {
        this.$ekmtcCommon.alertDefault('Please select at least one lithium battery option.')
        return
      }
      const btrCd = this.noLithiumYn === 'Y' ? '00' : this.findCombinationCode()
      if (!btrCd) {
        this.$ekmtcCommon.alertDefault('The selected lithium battery combination is not registered in CS139.')
        return
      }
      this.$emit('selectFunc', {
        btrCd,
        btrTypeList: this.noLithiumYn === 'Y' ? [] : this.normalizeTypeCodes(this.selectedTypeCodes),
        btrInclYn: this.noLithiumYn === 'Y' ? 'N' : 'Y'
      })
    }
  }
}
</script>

<style scoped>
.lithium_battery_desc { margin-bottom:14px; color:#222; font-size:14px; line-height:1.45; }
.lithium_battery_box { border:1px solid #d4d7df; }
.lithium_battery_options li + li { margin-top:9px; }
.lithium_battery_options label { color:#222; }
</style>
